import type { CustomerInfo, OrderInfo, Courier, Assembler, Supervisor, Operator } from '@@partner:types:app'
import type { CommonPager, NestedFilter } from '@@partner:types:graphql'

import { gql, useLazyQuery } from '@apollo/client'

import {
  SHORTCUT_CUSTOMERS_FIELDS,
  SHORTCUT_EMPLOYEE_FIELDS,
  SHORTCUT_PAGE_INFO_FIELDS
} from '@@partner:graphql/shortcuts'

export interface UniversalSearchResult {
  customers?: {
    edges: CustomerInfo[]
    pageInfo: {
      total: number
    }
  }
  orders?: {
    edges: OrderInfo[]
    pageInfo: {
      total: number
    }
  }
  couriers?: {
    edges: Courier[]
    pageInfo: {
      total: number
    }
  }
  assemblers?: {
    edges: Assembler[]
    pageInfo: {
      total: number
    }
  }
  supervisors?: {
    edges: Supervisor[]
    pageInfo: {
      total: number
    }
  }
  operators?: {
    edges: Operator[]
    pageInfo: {
      total: number
    }
  }
}

export interface UniversalSearchVariables {
  pager?: CommonPager
  customerFilter?: NestedFilter
  orderFilter?: NestedFilter
  employeeFilter?: NestedFilter
}

export const GQL_QUERY_UNIVERSAL_SEARCH = gql`
  query UniversalSearch(
    $pager: CommonPagerInput
    $customerFilter: NestedFilterInput
    $orderFilter: NestedFilterInput
    $employeeFilter: NestedFilterInput
  ) {
    customers: allCustomers(
      filter: $customerFilter
      pager: $pager
    ) {
      edges {
        ${SHORTCUT_CUSTOMERS_FIELDS}
      }
      pageInfo {
        total
      }
    }
    
    orders: allOrders(
      filter: $orderFilter
      pager: $pager
    ) {
      edges {
        id
        status
        shop_id
        customer_phone
        total
        address
        created_at
        fullName
        shop {
          id
          name
          address
        }
        customer {
          id
          first_name
          last_name
          fullName
        }
        assembler {
          id
          first_name
          last_name
          fullName
        }
        courier {
          id
          first_name
          last_name
          fullName
        }
      }
      pageInfo {
        total
      }
    }
    
    couriers: allCouriers(
      filter: $employeeFilter
      pager: $pager
    ) {
      edges {
        ${SHORTCUT_EMPLOYEE_FIELDS}
      }
      pageInfo {
        total
      }
    }
    
    assemblers: allAssemblers(
      filter: $employeeFilter
      pager: $pager
    ) {
      edges {
        ${SHORTCUT_EMPLOYEE_FIELDS}
        shop {
          id
          name
        }
      }
      pageInfo {
        total
      }
    }
    
    supervisors: allSupervisors(
      filter: $employeeFilter
      pager: $pager
    ) {
      edges {
        ${SHORTCUT_EMPLOYEE_FIELDS}
        email
        shops {
          id
          name
        }
      }
      pageInfo {
        total
      }
    }
    
    operators: allOperators(
      filter: $employeeFilter
      pager: $pager
    ) {
      edges {
        ${SHORTCUT_EMPLOYEE_FIELDS}
        email
      }
      pageInfo {
        total
      }
    }
  }
`

export const useUniversalSearch = () => {
  return useLazyQuery<UniversalSearchResult, UniversalSearchVariables>(
    GQL_QUERY_UNIVERSAL_SEARCH,
    {
      fetchPolicy: 'network-only'
    }
  )
}
